// Frontend TypeScript interfaces

export type Gender = "Man" | "Woman" | "Other";

// Badge types
export type BadgeType = 
  | "first_win"           // Won first match
  | "giant_killer"        // Beat a player ranked 5+ positions higher
  | "win_streak_10"       // Won 10 games in a row
  | "division_champion"   // Finished #1 in division
  | "close_call"          // Won a match 4-3 or 5-4
  | "perfect_day"         // Won all 4 matches in a league day
  | "veteran"             // Played 50+ league days
  | "partner_carry"       // Win with partner ranked 10+ positions lower
  | "clean_sweep"         // Win 5-0 or 6-0
  | "mr_ms_consistent"    // Win exactly 2 games in 5 consecutive matches
  | "century"             // Win 100 games in a season with positive game difference
  | "iron_man"            // Play 10 consecutive league days
  | "all_season_warrior"  // Play every league day in a season
  | "division_hopper"     // Play in 3+ different divisions
  | "unlucky"             // Lose 5 matches by close margins
  | "sp_team";            // SP Team member (manually assigned)

export interface Badge {
  type: BadgeType;
  earnedAt: Date;
  seasonId?: string;       // Season in which this badge was earned
  pointsAwarded?: number;  // Points credited when this badge was earned
  metadata?: {
    opponentName?: string;      // For giant_killer
    rankDifference?: number;    // For giant_killer, partner_carry
    leagueDayId?: string;       // For perfect_day, clean_sweep
    division?: Division;        // For division_champion
    streakLength?: number;      // For win_streak_10
    totalDays?: number;         // For veteran, all_season_warrior
    totalGames?: number;        // For century
    gameDifference?: number;    // For century
    partnerName?: string;       // For partner_carry
    partnerRank?: number;       // For partner_carry
    playerRank?: number;        // For partner_carry
    consecutiveMatches?: number; // For mr_ms_consistent
    consecutiveDays?: number;   // For iron_man
    seasonId?: string;          // For century, all_season_warrior
    divisions?: string[];       // For division_hopper
    closeLosses?: number;       // For unlucky
  };
}

// Division types (for league play)
export type Division = "Master" | "Division 1" | "Division 2" | "Division 2A" | "Division 2B" | "Division 3" | "Division 4" | "Women Division" | "Women Division 2" | "Test" | "Open" | "Beginner" | "Advanced" | "Intermediate" | "All" | "unassigned";

// SPL-specific divisions
export const SPL_DIVISIONS: Division[] = ["unassigned", "Master", "Division 1", "Division 2", "Division 2A", "Division 2B", "Division 3", "Division 4", "Women Division", "Women Division 2", "Test"];

// Padel Eagle-specific divisions
export const PADEL_EAGLE_DIVISIONS: Division[] = ["All", "unassigned", "Open", "Advanced", "Intermediate", "Beginner"];

// Tournament Level types (for tournament play)
export type TournamentLevel = "Beginner" | "Advanced" | "Intermediate" | "Open" | "Mixed" | "Women" | "All";

export const TOURNAMENT_LEVELS: TournamentLevel[] = ["Beginner", "Advanced", "Intermediate", "Open", "Mixed", "Women"];

/** Playing-strength range (min, max) for the levels that have one. */
export const TOURNAMENT_LEVEL_RANGES: Partial<Record<string, [number, number]>> = {
  Beginner: [0.5, 2.4],
  Intermediate: [2.5, 3.4],
  Advanced: [3.5, 4.4],
};

/** "0.5 - 2.4" for one level; the combined span for several (e.g. Beginner + Intermediate = "0.5 - 3.4"). Null when no level has a range. */
export const getLevelRangeLabel = (levels: string[]): string | null => {
  const ranges = levels.map((l) => TOURNAMENT_LEVEL_RANGES[l]).filter(Boolean) as [number, number][];
  if (ranges.length === 0) return null;
  const min = Math.min(...ranges.map((r) => r[0]));
  const max = Math.max(...ranges.map((r) => r[1]));
  return `${min.toFixed(1)} - ${max.toFixed(1)}`;
};

/** All levels a tournament is open to (falls back to the single legacy `level`). */
export const getTournamentLevels = (t: { level?: TournamentLevel; levels?: TournamentLevel[] }): TournamentLevel[] =>
  t.levels && t.levels.length > 0 ? t.levels : t.level ? [t.level] : [];

// All divisions combined (for backward compatibility)
export const DIVISIONS: Division[] = ["Master", "Division 1", "Division 2", "Division 3", "Women Division", "Women Division 2", "Test", "Open", "Beginner", "Advanced", "Intermediate", "All"];

export interface DivisionMovement {
  fromDivision: Division;
  toDivision: Division;
  movedAt: Date;
  movedBy?: string; // Admin who made the change
  reason?: string; // Optional reason for the move
}

export interface Player {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  nationality?: string;
  gender?: Gender;
  profilePicture?: string; // Firebase Storage URL
  division: Division;
  level?: number; // Skill level for tournament organizations (0-10)
  totalPoints: number;
  gamesPlayed: number;
  gamesWon: number;
  gamesLost: number;
  gameDifference: number; // gamesWon - gamesLost
  matchesWon: number;
  matchesLost: number;
  matchesInOtherDivisions?: number; // Number of matches played in divisions other than their registered division
  paidLeagueDaysBySeason?: { [seasonId: string]: number[] }; // Per-season array of within-season league day numbers that have been paid for (6, 7, 8, etc.)
  trialDaysCount?: number; // Number of trial days attended (for finance calculations only)
  isDisqualified?: boolean; // If true, player is excluded from rankings
  isGuest?: boolean; // If true, player pays €20 per league day from day 1 (no free days)
  isManagementTeam?: boolean; // If true, player is part of SP Team (management)
  autoRegisterLeagueDay?: boolean; // If true, player is auto-registered for league days (without SP Team badge)
  createdAt: Date;
  isActive: boolean;
  previousRank?: number; // Rank from previous league day (for showing movement)
  rankChange?: number; // Change in rank: positive = moved up, negative = moved down
  badges?: Badge[]; // Achievement badges earned by the player
  unseenBadges?: string[]; // Array of badge types the player hasn't seen yet (for celebration popup)
  divisionHistory?: DivisionMovement[]; // History of division changes
  divisionStats?: {
    [division: string]: {
      totalPoints: number;
      gamesPlayed: number;
      gamesWon: number;
      gamesLost: number;
      matchesWon: number;
      matchesLost: number;
      leagueDaysPlayed: number;
    };
  };
  seasonStats?: {
    [seasonId: string]: {
      division: Division;
      totalPoints: number;
      gamesPlayed: number;
      gamesWon: number;
      gamesLost: number;
      matchesWon: number;
      matchesLost: number;
      divisionStats?: {
        [division: string]: {
          totalPoints: number;
          gamesPlayed: number;
          gamesWon: number;
          gamesLost: number;
          matchesWon: number;
          matchesLost: number;
        };
      };
    };
  };
  
  // Player authentication fields
  hasAccount?: boolean; // True if player has created an account
  username?: string; // Unique username for login
  firstName?: string; // First name from account registration
  lastName?: string; // Last name from account registration
  accountCreatedAt?: Date; // When account was created/approved
  lastLoginAt?: Date; // Track last login
  lastLoginIp?: string; // Most recent login IP (used by admins to block abusive IPs)
  loginIpHistory?: Array<{ ip: string; at: string }>; // Deduped, most-recent-first list of IPs this account has logged in from (max 20)
  autoBlockIp?: boolean; // If true, every future login from this account blocks its IP and denies the login
  requirePasswordChange?: boolean; // True if using temporary password
  approvalType?: 'manual' | 'automatic'; // How the account was approved
  
  // Privacy and GDPR compliance fields
  displayName?: string; // Optional public display name (shown instead of real name)
  privacyPolicyAcceptedAt?: Date; // When player accepted privacy policy (GDPR requirement)
  hideFromPublicRankings?: boolean; // If true, player is hidden from public leaderboards
  
  // Administrative fields
  adminNote?: string; // Administrative note visible only to admins (e.g., special requests, contact preferences)
  currentSeason?: string; // Season ID this player is currently registered in (set by season wizard)
  allowedDivisions?: string[]; // Extra divisions the player is permitted to register in (beyond their primary)
  isTournamentOnly?: boolean; // If true, account was created via tournament registration — no access to league days
  isBanned?: boolean; // If true, player cannot register for league days, tournaments, or new seasons (DSGVO compliance - can still view stats and request data)
  bannedAt?: Date; // When player was banned
  bannedReason?: string; // Reason for banning (visible only to admins)
}

export interface PlayerNumberAssignment {
  playerId: string;
  playerName: string;
  assignedNumber: number; // 1-12
}

export interface GamePair {
  player1Id: string;
  player2Id: string;
  player1Name?: string; // Optional, populated by some API endpoints
  player2Name?: string; // Optional, populated by some API endpoints
}

export interface GameScore {
  pair1Games: number;
  pair2Games: number;
  isCompleted: boolean;
  winnerPair: 1 | 2 | null; // which pair won (1 or 2)
}

export interface Match {
  id: string;
  leagueDayId: string;
  seasonId?: string; // Season this match belongs to
  isTrial?: boolean; // True if this match belongs to a trial day (scores don't count toward ranking)
  division: Division; // Can be derived from LeagueDay but stored for easier queries
  roundNumber: number; // 1, 2, 3, or 4
  courtNumber: number; // 1, 2, or 3
  pair1: GamePair;
  pair2: GamePair;
  score: GameScore;
  maxGames: number; // 7 for rounds 1-3, 9 for round 4
  isCompleted: boolean;
  scoredBy?: string; // Scorekeeper ID who entered the score
  scoredAt?: Date; // When the score was entered
  createdAt: Date;
}

export interface Round {
  roundNumber: number;
  matches: Match[]; // Always 3 matches (one per court)
  isCompleted: boolean;
}

export interface PlayerSubstitution {
  originalPlayerId: string;
  originalPlayerName: string;
  substitutePlayerId: string;
  substitutePlayerName: string;
  rounds?: number[];
  timestamp: Date;
}

export interface LeagueDay {
  id: string;
  name?: string; // Optional custom name for the league day (e.g., "Spring Championship", "Trial Session")
  division: Division;
  date: Date;
  time?: string; // Optional time in HH:mm format (e.g., "18:00")
  courtCount: number; // 2, 3, 4, or 5 courts
  selectedPlayers: string[]; // 8, 12, 16, or 20 player IDs based on courtCount
  playerAssignments?: PlayerNumberAssignment[]; // Random number assignments (optional for drafts)
  rounds?: Round[]; // 4 rounds for regular, 6 rounds for trial (optional for drafts)
  currentRound: number; // 1, 2, 3, or 4 (or 1-6 for trial days)
  isTrial?: boolean; // True if this is a trial day (point-based scoring, no stats updates)
  scoringFormat?: 'games' | 'points'; // 'games' for best-of-7/9, 'points' for trial days
  isCompleted: boolean;
  isDraft?: boolean; // True if league day is a draft (no assignments/matches yet)
  isCancelled?: boolean; // True if an admin cancelled this league day (only shown in admin view)
  cancellationReason?: string;
  cancelledAt?: Date;
  substitutions?: PlayerSubstitution[]; // Track player substitutions
  createdAt: Date;
  seasonId?: string; // e.g., "winter-2025-26" - links league day to season
  
  // Partnership swap audit trail (for transparency)
  swapAuditTrail?: SwapAuditEntry;

  // Number of times the admin (re)ran the random draw before accepting these
  // assignments, e.g. via "Generate New Assignment" in RandomGeneratorDialog.
  // 1 means the first draw was used as-is; surfaced in the admin UI for transparency.
  assignmentGenerationCount?: number;

  // Court booking fields
  assignedCourt?: string; // Which court this division is assigned to (e.g., "1", "2", "3")
  availableCourts?: string[]; // All courts available for this time slot (e.g., ["1", "2", "3", "4", "5"])
  startTime?: string; // Session start time (e.g., "09:00")
  endTime?: string; // Session end time (e.g., "12:00")
  
  // Registration fields
  registrationOpen?: boolean; // True if players can register
  registrationOpenedAt?: Date; // When registration was opened
  registrationOpenedBy?: string; // Admin ID who opened registration
  registrationScheduledFor?: Date; // When registration should automatically open
  playerLimit?: number; // Max players for selectedPlayers (e.g., 12)
  waitlistPlayerIds?: string[]; // Players who registered after limit reached
  selectedPlayerNames?: string[]; // Resolved display names for selectedPlayers, parallel array (public endpoints only)
  waitlistPlayerNames?: string[]; // Resolved display names for waitlistPlayerIds, parallel array (public endpoints only)

  // Venue fields (denormalized from the Venue doc at write time, so history
  // stays accurate even if a venue's address is edited later)
  venueId?: string; // References a doc in the org's 'venues' collection
  venueName?: string;
  venueAddress?: string;
}

export interface Venue {
  id: string;
  name: string;
  address: string;
  createdAt: Date;
  updatedAt?: Date;
}

// Partnership swap audit trail for transparency
export interface PlayerNumberChange {
  playerId: string;
  playerName: string;
  originalNumber: number;
  finalNumber: number;
  resolvedConflicts: string[]; // Names of partners no longer paired with
}

export interface SwapAuditEntry {
  timestamp: Date;
  originalAssignments: PlayerNumberAssignment[];
  detectedRepeats: PartnershipConflict[];
  swapsPerformed: SwapAction[];
  finalAssignments: PlayerNumberAssignment[];
  playerChanges: PlayerNumberChange[]; // Summary of changes per player
  remainingConflicts: PartnershipConflict[]; // Conflicts that couldn't be resolved
  reason: string;
}

export interface PartnershipConflict {
  player1Id: string;
  player2Id: string;
  player1Name: string;
  player2Name: string;
  lastPlayedDate: Date;
  daysAgo: number;
  leagueDayId: string;
  frequency: number;
}

export interface SwapAction {
  swappedPlayer1Id: string;
  swappedPlayer1Name: string;
  swappedPlayer1OriginalNumber: number;
  swappedPlayer1NewNumber: number;
  swappedPlayer2Id: string;
  swappedPlayer2Name: string;
  swappedPlayer2OriginalNumber: number;
  swappedPlayer2NewNumber: number;
  resolvedConflict: PartnershipConflict;
  reason: string;
}

export interface SwapConfiguration {
  enabled: boolean;
  lookbackDays?: number; // DEPRECATED: Not used
  lookbackLeagueDays: number;
  maxSwapsPerDay?: number; // DEPRECATED: Now unlimited
}

// Court slot for 1.5-hour time slot assignment
export interface CourtSlot {
  slotStartTime: string; // HH:mm format (e.g., "09:00")
  slotEndTime: string; // HH:mm format (e.g., "10:30")
  division?: Division; // Assigned division
  leagueDayId?: string; // Associated league day ID
  autoBooked?: boolean; // Court slot was booked automatically by "copy every N weeks"
}

// Court booking for batch court management
export interface CourtBooking {
  id: string;
  organizationId: string;
  date: string; // YYYY-MM-DD format
  courtsAvailable: number[]; // Array of court numbers available (e.g., [1, 2, 3])
  startTime: string; // HH:mm format (e.g., "09:00")
  endTime: string; // HH:mm format (e.g., "12:00")
  // Court slots: each court gets array of 1.5-hour slots
  courtSlots?: {
    [courtNumber: number]: CourtSlot[];
  };
  notes?: string; // Optional notes about the booking
  venueId?: string; // A league day happens in one venue, so all bookings on a date share it
  venueName?: string; // Denormalized from the venue
  venueAddress?: string; // Denormalized from the venue
  createdAt: Date;
  updatedAt: Date;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  user: {
    id: string;
    username: string;
    role: "admin" | "scorekeeper" | "player";
    playerId?: string; // For player role, links to player profile
    playerName?: string; // For player role
    division?: Division; // For player role
    requirePasswordChange?: boolean; // For temporary passwords
    privacyPolicyAccepted?: boolean; // For player role - GDPR compliance
    isSuperAdmin?: boolean; // For admin role - super admins can manage permissions
    permissions?: string[]; // For admin role - array of permitted menu items
  };
}

export interface Scorekeeper {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  status: "pending" | "approved" | "rejected" | "revoked";
  createdAt: Date;
  approvedBy?: string;
  approvedAt?: Date;
  revokedBy?: string;
  revokedAt?: Date;
}

export interface ScorekeeperRequest {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  status: "pending" | "approved" | "rejected";
  createdAt: Date;
  reviewedBy?: string;
  reviewedAt?: Date;
}

export interface ScorekeeperRegistration {
  username: string;
  password: string;
  firstName: string;
  lastName: string;
}

// Player account registration request
export interface PlayerAccountRequest {
  id: string;
  username: string;
  name: string; // Full name from registration
  email: string;
  phone?: string;
  level?: number;
  linkedPlayerId: string; // Player profile they want to connect to
  linkedPlayerName?: string; // Name of the player profile (enriched by backend)
  division?: string; // Division of linked player (enriched by backend)
  requestType?: string; // e.g., 'tournament', 'season'
  invitationId?: string; // Tournament invitation ID if applicable
  status: "pending" | "approved" | "rejected";
  createdAt: Date;
  reviewedBy?: string; // Admin ID who reviewed
  reviewedAt?: Date;
  rejectionReason?: string; // Optional reason for rejection
}

// Password reset request
export interface PasswordResetRequest {
  id: string;
  playerId: string; // The player requesting password reset
  playerName: string;
  username: string;
  mobileNumber?: string; // WhatsApp contact number
  status: "pending" | "completed" | "expired";
  resetToken?: string; // Secure token for password reset link
  temporaryPassword?: string; // Shown to admin once - DEPRECATED
  createdAt: Date;
  completedBy?: string; // Admin ID who processed the request
  completedAt?: Date;
  expiresAt?: Date; // Reset token or temporary password expiration
}

export interface PlayerStats {
  playerId: string;
  playerName: string;
  profilePicture?: string;
  division: Division; // Player's registered division
  isGuestPlayer?: boolean; // True if player is from another division but played matches in this division
  isGuest?: boolean; // True if player is a permanent guest (pays €20 per league day)
  isManagementTeam?: boolean; // True if player is part of the management team
  totalPoints: number;
  gamesPlayed: number;
  gamesWon: number;
  gamesLost: number;
  gameDifference: number;
  matchesWon: number;
  matchesLost: number;
  leagueDaysPlayed: number;
  winPercentage: number;
  bayesianWinRate?: number; // Adjusted game win rate using Bayesian average (accounts for sample size)
  matchWinPercentage?: number; // Simple match win percentage (matchesWon / totalMatches)
  bayesianMatchWinRate?: number; // Adjusted match win rate using Bayesian average
  hybridScore?: number; // Hybrid ranking: 50% participation percentile + 50% Bayesian win rate
  averagePointsPerGame: number;
  matchesInOtherDivisions?: number; // Number of matches played in divisions other than their registered division
  badges?: Badge[]; // Achievement badges earned by the player
  privacyPolicyAccepted?: boolean; // GDPR: Whether player has accepted privacy policy
  rankChange?: number; // Change in rank: positive = moved up, negative = moved down
  // Tournament-specific fields
  totalTournamentPoints?: number; // Total points from tournament wins
  tournamentPoints?: { [tournamentId: string]: number }; // Points from each tournament
  tournamentsWon?: number; // Number of tournaments won
  // Admin "Match Public" preview only
  penaltyPoints?: number; // Total penalty points on record for the player
  actualRank?: number; // Position in the real public rankings (what-if view only)
  actualTotalPoints?: number; // Points in the real public rankings (what-if view only)
}

export interface LeagueDaySetup {
  division: Division;
  selectedPlayerIds: string[];
  date: Date;
}

export interface OverviewStats {
  totalPlayers: number;
  totalLeagueDays: number;
  totalMatches: number;
  completedMatches: number;
  completionRate: number;
}

export interface DivisionStats extends OverviewStats {
  division: string;
}

export interface AllDivisionsOverview {
  totalStats: OverviewStats;
  divisionBreakdown: DivisionStats[];
}

// For the random number generator
export interface RandomGeneratorStep {
  step: number;
  description: string;
  playerName?: string;
  assignedNumber?: number;
}

export interface RandomGenerationResult {
  assignments: PlayerNumberAssignment[];
  steps: RandomGeneratorStep[];
  seed: string; // For reproducibility and transparency
  generationCount?: number; // How many times "Start/Generate" was run before this draw was accepted
}

// Player History for Profile Modal
export interface PlayerHistoryMatch {
  id: string;
  leagueDayId: string;
  division: Division;
  isTrial?: boolean; // True if this match belongs to a trial day
  roundNumber: number;
  courtNumber: number;
  pair1: GamePair;
  pair2: GamePair;
  score: GameScore;
  isCompleted: boolean;
  pair1Player1Name: string;
  pair1Player2Name: string;
  pair2Player1Name: string;
  pair2Player2Name: string;
  scoredAt?: Date | string; // When the score was entered
  createdAt?: Date | string; // When the match was created
}

export interface LeagueDayStats {
  leagueDayId: string;
  date: any; // Firestore Timestamp
  points: number;
  dayNumber: number;
  participated?: boolean; // True if player participated, false if they were absent
}

export interface PartnershipStats {
  partnerId: string;
  partnerName: string;
  gamesPlayed: number;
  gamesWon: number;
  winRate: number;
}

export interface OpponentStats {
  opponentId: string;
  opponentName: string;
  gamesPlayed: number;
  gamesLost: number;
  lossRate: number;
}

// Partnership stats for best combos
export interface PartnershipCombo {
  player1Id: string;
  player2Id: string;
  player1Name: string;
  player2Name: string;
  player1ProfilePicture?: string;
  player2ProfilePicture?: string;
  player1PrivacyAccepted?: boolean;
  player2PrivacyAccepted?: boolean;
  gamesPlayed: number;
  gamesWon: number;
  winRate: number;
}

// Attendance leaders
export interface AttendanceLeader {
  playerId: string;
  playerName: string;
  profilePicture?: string;
  attendedDays: number;
  totalLeagueDays: number;
  attendanceRate: number;
  currentStreak: number;
  maxStreak: number;
}

// Records
export interface RecordItem {
  playerId?: string;
  playerName?: string;
  profilePicture?: string;
  privacyPolicyAccepted?: boolean;
  leagueDayId?: string;
  wins?: number;
  gamesWon?: number;
  gamesLost?: number;
  gameDifference?: number;
  score?: string;
  totalGames?: number;
  pair1Player1?: string;
  pair1Player2?: string;
  pair2Player1?: string;
  pair2Player2?: string;
  perfectDaysCount?: number;
  totalGamesWon?: number;
  totalGamesLost?: number;
  totalGamesPlayed?: number;
  totalMatchesWon?: number;
  totalMatchesLost?: number;
  totalMatchesPlayed?: number;
  winPercentage?: number;
}

export interface DivisionRecords {
  bestGameDifference: RecordItem[];
  overallGameDifference: RecordItem[];
  gameWinPercentage: RecordItem[];
  matchWinPercentage: RecordItem[];
  partnerships: PartnershipCombo[];
  perfectDays: RecordItem[];
}

// Division comparison
export interface DivisionComparison {
  division: string;
  totalPlayers: number;
  totalLeagueDays: number;
  totalMatches: number;
  avgPointsPerPlayer: number;
  avgGamesPerPlayer: number;
  topPlayer: string;
  topPlayerPoints: number;
  pointSpread: number;
  recentActivity: number;
}

export interface PlayerHistory {
  matches: PlayerHistoryMatch[];
  leagueDays: LeagueDay[];
  leagueDayStats: LeagueDayStats[];
  allDivisionLeagueDays?: {
    id: string;
    date: string;
    isCompleted: boolean;
    isDraft: boolean;
    participated: boolean;
    dayNumber: number;
  }[];
  divisionStats?: {
    [division: string]: {
      totalPoints: number;
      gamesPlayed: number;
      gamesWon: number;
      gamesLost: number;
      matchesWon: number;
      matchesLost: number;
      leagueDaysPlayed: number;
    };
  };
  penalties?: {
    id: string;
    playerId: string;
    division: string;
    points: number;
    reason: string;
    type: 'penalty' | 'bonus';
    createdAt: string;
    seasonId?: string;
  }[];
}

// Nations League Types
export type NationsLeagueGroup = 'A' | 'B' | 'C';
export type NationsLeagueStage = 'group' | 'semifinal_top' | 'final_top' | 'semifinal_bottom' | 'final_bottom' | 'third_place_top' | 'third_place_bottom';

export interface NationsLeaguePlayer {
  name: string;
  profilePicture?: string; // Firebase Storage URL
  playerId?: string; // linked SPL player account (unset for players without an account)
}

export interface NationsLeagueTeam {
  id: string;
  nationsLeagueId: string;
  name: string;
  flag: string; // emoji flag(s)
  group?: NationsLeagueGroup; // unset until the groups are drawn
  isBye?: boolean; // placeholder team (odd number of nations) - occupies a group slot but plays no matches
  players?: (NationsLeaguePlayer | string)[]; // Array of player objects or strings (backward compatibility)
  rosterLocked?: boolean; // True once the initial roster was confirmed as final
  rosterLockedAt?: string;  rosterChanges?: { group: number; knockout: number }; // Counted changes used per phase
}

export interface NationsLeagueRosterHistoryEntry {
  id: string;
  teamId: string;
  type: 'initial' | 'change' | 'bypass';
  phase: 'group' | 'knockout';
  previousPlayers: (NationsLeaguePlayer | string)[];
  newPlayers: NationsLeaguePlayer[];
  reason?: string;
  createdAt: string;
}

// Admin view of a match day; the public view only has availability (when revealed)
export interface NationsLeagueMatchDay {
  id: string;
  date: string; // YYYY-MM-DD
  payments: Record<string, { paid: boolean; paidAt?: string | null; note?: string }>;
  availability: Record<string, string[]>;
  availabilityRevealed: boolean;
}

export interface NationsLeagueSet {
  team1Games: number;
  team2Games: number;
  isSuperTiebreak: boolean; // true for 3rd set (to 10 instead of 6)
}

// Individual match within a matchup (3 matches per matchup)
export interface NationsLeagueIndividualMatch {
  matchNumber: 1 | 2 | 3; // Match 1, 2, or 3 within the matchup
  sets: [NationsLeagueSet, NationsLeagueSet, NationsLeagueSet]; // 3 sets per match (set 3 is super tiebreak)
  isCompleted: boolean;
  winnerTeamId: string | null;
  // Player assignments (optional, assigned by admin)
  team1Player1?: string; // Player name from team1's roster
  team1Player2?: string; // Player name from team1's roster
  team2Player1?: string; // Player name from team2's roster
  team2Player2?: string; // Player name from team2's roster
  // Did not compete flags (optional)
  team1DidNotCompete?: boolean; // True if team1 did not compete, results in automatic loss
  team2DidNotCompete?: boolean; // True if team2 did not compete, results in automatic loss
  // Super tiebreak flag (for Match 3 in semifinals/finals when first 2 matches are 1-1)
  isSuperTiebreakDecider?: boolean; // True if this match is played as a super tiebreak to 10 with mixed players
}

// Matchup between two teams (contains 3 individual matches)
export interface NationsLeagueMatch {
  id: string;
  nationsLeagueId: string;
  team1Id: string;
  team2Id: string;
  team1Name: string;
  team2Name: string;
  team1Flag: string;
  team2Flag: string;
  group?: NationsLeagueGroup; // Only for group stage
  stage: NationsLeagueStage;
  matches: [NationsLeagueIndividualMatch, NationsLeagueIndividualMatch, NationsLeagueIndividualMatch]; // 3 matches
  isCompleted: boolean; // True when all 3 matches are complete
  isDraw?: boolean; // True if matchup was manually marked as a draw
  status?: 'Scheduled' | 'In Progress' | 'Completed' | 'Completed (Draw)'; // Status of the matchup
  team1MatchesWon: number; // How many of the 3 matches team1 won
  team2MatchesWon: number; // How many of the 3 matches team2 won
  winnerTeamId: string | null; // Winner of the matchup (best of 3 matches)
  matchNumber?: number; // For ordering
  matchDateTime?: Date | null; // Date and time for the matchup
  createdAt: Date;
  updatedAt?: Date;
}

export interface NationsLeagueCountryInput {
  name: string;
  isoCode: string; // 2-letter code, or 'ROW' for Rest of the World
  group?: NationsLeagueGroup;
}

export interface NationsLeagueFaqItem {
  category?: string;
  question: string;
  answer: string;
}

export interface NationsLeagueEvent {
  id: string;
  name: string; // e.g. "SPCup of Nations 26/27"
  status: 'active' | 'completed';
  isLocked: boolean;
  announcement: { message: string; isEnabled: boolean };
  description?: string;
  faq?: NationsLeagueFaqItem[];
  countries: NationsLeagueCountryInput[]; // denormalized, for list cards
  createdAt: Date;
  createdBy: string;
}

export interface NationsLeagueStandings {
  teamId: string;
  teamName: string;
  flag: string;
  group: NationsLeagueGroup;
  matchesPlayed: number;
  matchesWon: number;
  matchesDrawn: number;
  matchesLost: number;
  setsWon: number;
  setsLost: number;
  setDifference: number;
  gamesWon: number;
  gamesLost: number;
  gameDifference: number;
  points: number; // 3 for win, 0 for loss
  position: number;
}

// Announcement Types
export interface Announcement {
  id: string;
  message: string;
  isEnabled: boolean;
  backgroundColor?: string; // Color for the marquee background
  linkUrl?: string;
  linkLabel?: string;
  createdAt: Date;
  updatedAt?: Date;
}

// Player pop-up announcements — visual style + audience targeting
export type PopupStyle = 'notice' | 'congrats' | 'important' | 'update' | 'neutral_teal' | 'neutral_indigo' | 'neutral_rose' | 'neutral_slate';
export const POPUP_STYLES: PopupStyle[] = ['notice', 'congrats', 'important', 'update', 'neutral_teal', 'neutral_indigo', 'neutral_rose', 'neutral_slate'];
export type PopupTargetType = 'all' | 'players' | 'division';

// Shared admin-list shape for both the Player Pop-ups and Public Homepage
// Pop-ups tabs (PopupEditorPanel). Fields that don't apply to a given variant
// (e.g. targeting/acknowledgments for public pop-ups) are simply absent.
export interface PopupItem {
  id: string;
  title: string;
  message: string;
  imageUrl?: string;
  linkUrl?: string;
  linkLabel?: string;
  style?: PopupStyle;
  targetType?: PopupTargetType;
  targetPlayerIds?: string[];
  targetDivisions?: string[];
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  acknowledgedCount?: number;
  acknowledgments?: { playerId: string; playerName: string; acknowledgedAt: string }[];
}

// Season Types
export interface Season {
  id: string; // e.g., "winter-2025-26"
  name: string; // e.g., "Winter 2025/26"
  divisions: Division[]; // Array of divisions in this season
  startDate: Date;
  endDate: Date;
  status: 'draft' | 'active' | 'completed'; // Lifecycle status
  isActive: boolean; // Only one can be active at a time
  isArchived: boolean;
  previousSeasonPlayerCount?: number | null; // Frozen snapshot taken when this season was created as a draft
  previousSeasonDivisionCounts?: { [division: string]: number } | null; // Frozen per-division snapshot taken when this season was created as a draft
  previousSeasonDivisionPlayerIds?: { [division: string]: string[] } | null; // Frozen per-division player-ID roster, same snapshot
  createdAt: Date;
  createdBy: string; // Admin username
}

// Financial Transaction - per season
export interface FinancialTransaction {
  id: string;
  playerId: string;
  playerName: string;
  division: Division;
  leagueDayId: string;
  leagueDayNumber: number;
  leagueDayDate: Date;
  amount: number; // €18 for regular, €20 for guest
  isPaid: boolean;
  seasonId: string; // e.g., "winter-2025-26"
  createdAt: Date;
  paidAt?: Date;
}

// Penalty or Bonus - per season
export interface PenaltyBonus {
  id: string;
  playerId: string;
  playerName: string;
  division: Division;
  points: number; // Negative for penalty, positive for bonus
  reason?: string;
  disqualificationReason?: string;
  type: 'penalty' | 'bonus';
  leagueDayAdjustment?: number; // +N for penalty days, -N for bonus days
  isDisqualification?: boolean;
  seasonId: string; // e.g., "winter-2025-26"
  createdAt: Date;
}

// Transfer - records which season the transfer occurred
export interface Transfer {
  id: string;
  playerId: string;
  playerName: string;
  playerProfilePicture?: string;
  fromDivision: Division;
  toDivision: Division;
  movedAt: Date;
  movedBy?: string; // Admin who made the change
  reason?: string;
  seasonId: string; // e.g., "winter-2025-26" - season when transfer happened
}

// Season Transition - for the start season wizard
export interface PlayerSeasonTransition {
  playerId: string;
  playerName: string;
  currentDivision: Division;
  newDivision: Division;
  shouldContinue: boolean;
  transitionType: 'promoted' | 'relegated' | 'stays' | 'manual' | 'deactivated';
  isGuest: boolean;
}

// Season Registration Types
export type GearOption = 'none' | 'tshirt-only' | 'shorts-only' | 'full-kit';

export interface GearTshirt {
  size: 'S' | 'M' | 'L' | 'XL' | 'XXL';
  shirtType: 'tshirt' | 'polo';
  color?: 'red' | 'white' | 'black'; // Legacy field, kept for backward compatibility with old registrations
  designId?: string; // References a GearDesign in the org's gear design catalog
  designName?: string; // Denormalized design name, kept even if the design is later deleted
  nameOnShirt?: string;
}

// Admin-managed design catalog (t-shirts, polos, and shorts), revealed to players after registration closes
export interface GearDesign {
  id: string;
  name: string;
  category: 'tshirt' | 'polo' | 'shorts';
  imageUrl?: string;
  active: boolean;
  sortOrder: number;
  createdAt: string | Date;
}

export interface GearShorts {
  size: 'S' | 'M' | 'L' | 'XL' | 'XXL';
  designId?: string; // References a GearDesign (category: 'shorts') in the org's gear design catalog
  designName?: string; // Denormalized design name, kept even if the design is later deleted
}

export interface GearSocks {
  size: string; // Shoe size number (e.g., "35", "42", "47")
}

export type DiscountType = 'percentage' | 'fixed';
export type DiscountScope = 'tournament' | 'season';
export type DiscountCodeStatus = 'active' | 'used' | 'revoked';

export interface DiscountCode {
  id: string;
  code: string; // unique, e.g. "SPL-7K2M"
  discountType: DiscountType;
  discountValue: number;
  scope: DiscountScope;
  scopeTargetId: 'any' | string; // tournament id, or season id, or 'any'
  scopeTargetName?: string; // denormalized label for admin list + season-name matching
  assignedPlayerId?: string | null;
  assignedPlayerName?: string | null;
  note?: string; // admin-only label, useful for unassigned/generic codes
  status: DiscountCodeStatus;
  createdAt: Date;
  createdByAdminId?: string | null;
  emailSentAt?: Date | null;
  seenAt?: Date | null; // player dismissed the gift popup
  usedAt?: Date | null;
  usedByPlayerId?: string | null;
  usedByPlayerName?: string | null;
  usedForId?: string | null; // tournamentId or seasonRegistration id actually redeemed against
}

export interface SeasonRegistration {
  id: string;
  playerName: string;
  email: string;
  phone: string;
  nationality: string;
  isDPVRegistered: boolean; // Deutscher Padel Verband
  wantsGear: boolean; // Interested in SPL gear
  gearOption?: GearOption; // What gear the player chose
  gearTshirts?: GearTshirt[]; // Array of t-shirts with sizes and optional names
  gearShorts?: GearShorts[]; // Array of shorts with sizes
  gearSocks?: GearSocks[]; // Array of socks with sizes
  gearTotalCost?: number; // Total cost of gear order
  // Legacy fields (kept for backward compatibility)
  tshirtSize?: 'S' | 'M' | 'L' | 'XL' | 'XXL';
  shortsSize?: 'S' | 'M' | 'L' | 'XL' | 'XXL';
  gearNameOnShirt?: string;
  // New player level preferences
  preferredDivisionLevel?: 'beginner' | 'beginner+' | 'intermediate' | 'advanced' | 'pro'; // Which level they prefer
  levelReason?: string; // Required explanation when preferredDivisionLevel is intermediate/advanced/pro
  playtomicLevel?: string; // Optional self-reported Playtomic level
  hasTournamentExperience?: boolean; // Whether they've participated in any tournaments
  hearAboutUsSource?: string; // Where they heard about SPL from
  hasReferencePlayer?: boolean; // Whether they regularly play with an existing SPL player
  referencePlayerName?: string; // Name of the SPL player who can vouch for their level
  gender?: Gender;
  wantsTrialDay?: boolean; // Whether they want to participate in trial day to prove higher level
  preferredDivision?: string; // Division player wants to play in (when dissatisfied with projection) - existing players
  preferredDivisionReason?: string; // Player's explanation for why they should play in preferredDivision
  // Projected division for existing players (snapshot from registration time)
  projectedDivision?: string; // The division they will play in next season
  projectionType?: 'promoted' | 'relegated' | 'stays'; // Whether they are promoted, relegated, or staying
  currentDivision?: string; // Their current division at time of registration
  createdAt: Date;
  status?: 'pending' | 'approved' | 'rejected' | 'needs_clarification';
  adminNote?: string; // Admin note for needs_clarification status
  assignedDivision?: string; // Admin-assigned division for next season (for planning)
  isExistingPlayer?: boolean; // Whether this is a player with an existing account
  playerId?: string; // Reference to Player document if existing player
  username?: string; // Username for new players creating accounts
  // Season invitation / confirmation link fields
  confirmationToken?: string; // Unique token for confirmation link
  paymentReference?: string; // Verwendungszweck for bank transfer
  invitationSentAt?: Date; // When invitation email was last sent
  confirmationSubmittedAt?: Date; // When player submitted confirmation form
  confirmationLocked?: boolean; // True after player submits – form locked
  trialDayResponse?: 'yes' | 'no'; // Player RSVP for trial day
  // Discount / voucher code
  voucherCode?: string; // Raw code entered by the player at registration time
  discountType?: DiscountType; // Set once the code is validated/consumed at invitation-send time
  discountValue?: number;
  discountCode?: string; // The actual code string that was redeemed
  discountAmount?: number; // Computed € amount deducted from the total
  registrationPhase?: 'initial' | 'mid_season'; // Which registration window this was submitted under (resolved server-side)
  seasonId?: string; // Season this registration is filed under (resolved server-side)
  // Confirmation email delivery tracking (set right after the send attempt at submit time)
  confirmationEmailSent?: boolean; // True only if the provider accepted the send
  confirmationEmailSentAt?: string; // ISO date string
  confirmationEmailError?: string; // Set when the send attempt failed or was skipped
}

export interface SeasonRegistrationRequest {
  playerName: string;
  email: string;
  phone: string;
  nationality: string;
  isDPVRegistered: boolean;
  wantsGear: boolean;
  gearOption?: GearOption;
  gearTshirts?: GearTshirt[];
  gearShorts?: GearShorts[];
  gearSocks?: GearSocks[];
  gearTotalCost?: number;
  gender?: Gender;
  // New player level preferences
  preferredDivisionLevel?: 'beginner' | 'beginner+' | 'intermediate' | 'advanced' | 'pro'; // Which level they prefer
  levelReason?: string; // Required explanation when preferredDivisionLevel is intermediate/advanced/pro
  playtomicLevel?: string; // Optional self-reported Playtomic level
  hasTournamentExperience?: boolean; // Whether they've participated in any tournaments
  hearAboutUsSource?: string; // Where they heard about SPL from
  hasReferencePlayer?: boolean; // Whether they regularly play with an existing SPL player
  referencePlayerName?: string; // Name of the SPL player who can vouch for their level
  wantsTrialDay?: boolean; // Whether they want to participate in trial day to prove higher level
  username?: string; // For new players creating accounts
  password?: string; // For new players creating accounts
  voucherCode?: string; // Raw code entered by the player at registration time
}

export interface ExistingPlayerRegistrationRequest {
  email: string;
  phone: string;
  nationality?: string;
  gender?: Gender;
  isDPVRegistered: boolean;
  wantsGear: boolean;
  gearOption?: GearOption;
  gearTotalCost?: number;
  wantsTrialDay?: boolean;
  preferredDivision?: string; // Division player wants to play in (when dissatisfied with projection)
  preferredDivisionReason?: string; // Player's explanation for why they should play in preferredDivision
  projectedDivision?: string; // The projected division calculated at registration time
  projectionType?: 'promoted' | 'relegated' | 'stays'; // Type of projection
  currentDivision?: string; // Current division at registration time
  gearTshirts?: GearTshirt[];
  gearSocks?: GearSocks[];
  gearShorts?: GearShorts[];
  acceptedCancellationPolicy?: boolean; // Whether player accepted cancellation policy
  cancellationPolicyAcceptedAt?: Date; // When cancellation policy was accepted
  voucherCode?: string; // Raw code entered by the player at registration time
  // Level-assessment questions asked instead of the division fields above when the
  // player has no division history yet (e.g. a tournament-only account).
  preferredDivisionLevel?: 'beginner' | 'beginner+' | 'intermediate' | 'advanced' | 'pro';
  levelReason?: string;
  playtomicLevel?: string;
  hasTournamentExperience?: boolean;
  hearAboutUsSource?: string;
  hasReferencePlayer?: boolean;
  referencePlayerName?: string;
}

// Admin Navigation Preferences
export interface AdminNavigationPreferences {
  menuOrder: string[]; // Array of menu item texts in custom order
  hiddenItems: string[]; // Array of menu item texts to hide
}

// Tournament types
export type TournamentFormat = 'single-elimination' | 'double-elimination' | 'round-robin' | 'groups-knockout' | 'americana';
export type TournamentStatus = 'upcoming' | 'registration' | 'group-stage' | 'knockout' | 'completed' | 'cancelled';
export type KnockoutRound = 'round-of-32' | 'round-of-16' | 'quarter-finals' | 'semi-finals' | 'final' | 'consolation-semi-finals' | 'third-place' | 'fifth-place' | 'seventh-place';
export type MatchScoringSystem = 'sets-games' | 'timed-points';
export type TournamentMatchFormat = 'best-of-7' | 'best-of-9' | 'best-of-11' | 'best-of-3' | 'best-of-5';
export type KnockoutType = 'masters-cup' | 'tour-cup';
export type KnockoutFormatType = 'double-bracket' | 'single-bracket'; // Double-bracket = Masters/Tour Cup, Single-bracket = Standard

// Uneven group/knockout advancement rules (double-bracket only). Evaluated in the order:
// mastersCupAdvancementRules first, then tourCupAdvancementRules — each rule's picks are
// removed from the pool before the next rule runs, so 'remaining' always sees the leftovers.
export type AdvancementRule =
  | { type: 'group-position'; position: number } // 0-indexed: every group's Nth place
  | { type: 'best-of-position-across-groups'; position: number; count: number } // top `count` teams at group-position `position`, ranked across ALL groups
  | { type: 'remaining' }; // catch-all: everyone not already claimed by an earlier rule, ranked
export type TournamentEventType = 'break' | 'ceremony' | 'tombola' | 'other'; // Types of non-match events

export interface TournamentEvent {
  id: string;
  name: string;
  type: TournamentEventType;
  startTime: string; // ISO time string
  duration: number; // Minutes
  description?: string;
}

// A court-block: reserves one court for a specific time window (e.g. an outside
// booking, maintenance, a sponsor activation). Behaves like a single match card in
// the schedule grid — organizers name it, then drag it into place like any match.
export interface ScheduleBlock {
  id: string;
  name: string; // custom label, shown wherever the schedule is displayed
  startTime: string; // ISO datetime string, same format as match.startTime
  courtNumber: number;
  duration: number; // Minutes
}

export interface TournamentTeam {
  id: string;
  player1Id: string;
  player2Id: string;
  player1Name?: string;
  player2Name?: string;
  teamName?: string;
  registeredAt?: Date | string;
  isBye?: boolean; // Placeholder team used to fill an uneven bracket/group — opponents auto-win
  isTestTeam?: boolean; // Virtual "test_" team used to mock a schedule; its players don't exist in the players collection
}

export interface TournamentPaymentWindow {
  startAt?: string;
  endAt?: string;
  price?: number | null;
}

export interface TournamentRefundPolicy {
  moreThanFiveDays?: string;
  betweenFiveAndTwoDays?: string;
  within48Hours?: string;
}

export interface TournamentGroup {
  id: string;
  name: string;
  teamIds: string[];
  standings?: {
    teamId: string;
    matchesPlayed: number;
    wins: number;
    draws: number;
    losses: number;
    gamesWon: number;
    gamesLost: number;
    gamesDiff: number;
    points: number;
  }[];
  matchResults?: {
    team1Id: string;
    team2Id: string;
    team1Games: number;
    team2Games: number;
    startTime?: string; // ISO time string for scheduled match start
    courtNumber?: number; // Court assignment
    isWalkover?: boolean; // Auto-resolved win against a Bye placeholder — never scheduled/scored manually
  }[];
}

export interface KnockoutMatch {
  id: string;
  round: KnockoutRound;
  knockoutType: KnockoutType;
  matchNumber: number;
  team1Id?: string;
  team2Id?: string;
  winnerId?: string;
  matchId?: string;
  nextMatchId?: string;
  nextMatchNumber?: number;
  previousMatch1?: number;
  previousMatch2?: number;
  isConsolationMatch?: boolean;
  team1Games?: number;
  team2Games?: number;
  startTime?: string; // ISO time string for scheduled match start
  courtNumber?: number; // Court assignment
  isPlaceholder?: boolean; // Reserved slot before actual bracket generation
  isCancelled?: boolean;    // Match was cancelled / removed from schedule
  team1Placeholder?: string; // Description of team1 for placeholder matches
  team2Placeholder?: string; // Description of team2 for placeholder matches
  isWalkover?: boolean; // Auto-resolved win against a Bye placeholder — never scheduled/scored manually
  team1SkippedFirstRound?: boolean; // team1Id advanced directly past Round 1 (structural bye, uneven bracket size)
  team2SkippedFirstRound?: boolean; // same, for team2Id
}

// Tournament invitation types
export type InvitationStatus = 'pending' | 'accepted' | 'accepted-auto' | 'declined' | 'expired' | 'canceled';
export type RegistrationMode = 'team-invitation' | 'looking-for-partner' | 'confirmed';

export interface TournamentInvitation {
  id: string;
  tournamentId: string;
  tournamentName: string;
  senderPlayerId: string;
  senderPlayerName: string;
  senderEmail: string;
  senderLevel?: number;
  senderProfilePicture?: string;
  recipientPlayerId?: string; // null if recipient is new player
  recipientEmail: string;
  recipientName?: string; // For new players who don't have accounts yet
  teamName?: string;
  personalMessage?: string;
  status: InvitationStatus;
  createdAt: Date;
  expiresAt: Date; // 48h timeout from creation
  respondedAt?: Date;
  declineReason?: string;
  teamRegistrationId?: string; // Link to created team registration
}

export interface PartnerBoardEntry {
  id: string;
  tournamentId: string;
  tournamentName: string;
  playerId: string;
  playerName: string;
  playerEmail: string;
  playerLevel?: number;
  profilePicture?: string;
  preferredPartnerLevelMin?: number;
  preferredPartnerLevelMax?: number;
  bio?: string; // Short message about playing style or preferences
  createdAt: Date;
  isActive: boolean; // false when partner is found and team confirmed
  isLocked?: boolean; // true if password-protected entry
  waitingForPartner?: boolean; // true if waiting for a specific partner to register
  partnerEmail?: string; // Email of partner they're waiting for
  partnerName?: string; // Name of partner they're waiting for
}

export interface TournamentRegistration {
  id: string;
  tournamentId: string;
  player1Id: string;
  player2Id: string;
  player1Name: string;
  player2Name: string;
  player1Level?: number;
  player2Level?: number;
  teamName?: string;
  status: 'pending-invitation' | 'confirmed' | 'waitlist' | 'withdrawn';
  invitationId?: string; // Link to invitation that created this registration
  mode: RegistrationMode;
  registeredAt: Date;
  confirmedAt?: Date;
  withdrawnAt?: Date;
  
  // New player flags
  player1IsNew?: boolean; // true if player1 created account via this invitation
  player2IsNew?: boolean; // true if player2 created account via this invitation
  adminReviewRequired?: boolean; // true if any player is new and needs level assessment
  adminReviewedAt?: Date;
  adminReviewedBy?: string;
  
  // Auto-confirmation tracking
  autoConfirmed?: boolean; // true if confirmed via reciprocal invitations
  autoConfirmedAt?: Date;
  withdrawalDeadline?: Date; // 24h grace period for auto-confirmed teams
}

// Americana format: a single-day, fixed-schedule round format (no groups, no bracket).
// Pairings rotate every round so there are no registered teams — matches reference
// individual player IDs directly via GamePair, same as LeagueDay matches do.
export interface AmericanaMatch {
  id: string;
  roundNumber: number;
  courtNumber: number;
  pair1: GamePair;
  pair2: GamePair;
  score: GameScore;
  isCompleted: boolean;
  pair1HandicapIds?: string[]; // AmericanaHandicapType ids applied to this specific match's pair1
  pair2HandicapIds?: string[]; // AmericanaHandicapType ids applied to this specific match's pair2
}

export interface AmericanaRound {
  roundNumber: number;
  matches: AmericanaMatch[];
}

export interface AmericanaHandicapType {
  id: string;
  label: string;
  emoji: string;
}

export interface AmericanaStanding {
  playerId: string;
  matchesPlayed: number;
  wins: number;
  draws: number;
  losses: number;
  gamesWon: number;
  gamesLost: number;
  gamesDiff: number;
  points: number;
  position?: number; // Set once the tournament is completed
}

export interface AmericanaSubstitution {
  id: string;
  originalPlayerId: string;
  originalPlayerName: string;
  substitutePlayerId: string;
  substitutePlayerName: string;
  isNewGuestPlayer: boolean; // true if substitutePlayerId was auto-created from a free-text name
  roundsAffected: number[];
  reason?: string;
  timestamp: Date;
}

export interface Tournament {
  id: string;
  name: string;
  description?: string;  venue?: string;  level: TournamentLevel; levels?: TournamentLevel[]; // level = primary (first) level; levels = all selected levels
  seasonId?: string;
  registrationOpenDate: Date;
  registrationCloseDate: Date;
  startDate: Date;
  startTime?: string; // ISO time string (e.g., "09:00") for tournament start time
  endDate?: Date;
  numberOfCourts?: number; // Number of courts available for parallel matches
  changeoverTime?: number; // Minutes between matches on the same court (default: 0)
  courtPricePerSlot?: number; // Price per 90-min slot per court (€)
  registrationFeePerTeam?: number; // Registration fee per team (€)
  events?: TournamentEvent[]; // Breaks, ceremonies, tombola, etc.
  scheduleBlocks?: ScheduleBlock[]; // Manually-placed court blocks (outside bookings, maintenance, etc.)
  status: TournamentStatus;
  format: TournamentFormat;
  scoringSystem: MatchScoringSystem;
  matchFormat?: 'best-of-3' | 'best-of-5'; // Legacy: use groupMatchFormat / knockoutMatchFormat instead
  groupMatchFormat?: TournamentMatchFormat; // Format for group stage matches
  knockoutMatchFormat?: TournamentMatchFormat; // Format for knockout stage matches
  timedMatchDuration?: number;
  registeredTeams: TournamentTeam[];
  maxTeams: number;
  minTeams: number;
  americanaPlayerIds?: string[]; // Roster picked for the event, before/while building americanaRounds
  americanaStrengthOrder?: string[]; // Player IDs sorted strongest -> weakest for balance analysis
  americanaRounds?: AmericanaRound[];
  americanaStandings?: AmericanaStanding[];
  americanaHandicapTypes?: AmericanaHandicapType[]; // Reusable catalog of handicap labels/emoji for this tournament
  americanaSubstitutions?: AmericanaSubstitution[]; // Audit trail of player substitutions applied during the event
  groups?: TournamentGroup[];
  teamsPerGroup?: number;
  knockoutFormatType?: KnockoutFormatType; // 'double-bracket' for Masters/Tour Cup, 'single-bracket' for standard
  mastersCupAdvanceCount?: number; // Used when knockoutFormatType is 'double-bracket'
  tourCupAdvanceCount?: number; // Used when knockoutFormatType is 'double-bracket'
  knockoutAdvanceCount?: number; // Used when knockoutFormatType is 'single-bracket' - how many teams advance from each group
  customMastersCupSeeding?: string[]; // Custom seeding order for Masters Cup bracket
  customTourCupSeeding?: string[]; // Custom seeding order for Tour Cup bracket
  customSingleBracketSeeding?: string[]; // Custom seeding order for single bracket
  mastersCupAdvancementRules?: AdvancementRule[]; // Optional cross-group advancement rules (double-bracket only); overrides mastersCupAdvanceCount when set
  tourCupAdvancementRules?: AdvancementRule[]; // Optional cross-group advancement rules (double-bracket only); overrides tourCupAdvanceCount when set
  mastersCupBracket?: KnockoutMatch[];
  tourCupBracket?: KnockoutMatch[];
  knockoutBracket?: {
    mastersCup?: KnockoutMatch[];
    tourCup?: KnockoutMatch[];
  };
  playAllPositions?: boolean;
  pointsPerWin: number;
  pointsPerDraw: number;
  pointsPerLoss: number;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  mastersCupWinnerId?: string;
  mastersCupRunnerUpId?: string;
  tourCupWinnerId?: string;
  
  // Registration management
  registrations?: TournamentRegistration[];
  pendingInvitationsCount?: number;
  confirmedTeamsCount?: number;
  spotsRemaining?: number;
  
  // Visibility control
  isVisibleToPlayers?: boolean; // If false, tournament is hidden from player registration views
  visibleToPlayerIds?: string[]; // If non-empty, only these players can see the tournament
  playerRegistrationManagedByAdmin?: boolean; // If true, players cannot self-register; admin manages all registrations
  posterImageUrl?: string; // Firebase Storage URL used as card background on public page
  sponsors?: TournamentSponsor[];
  isPointsRelevant?: boolean; // If false, winners earn no Road to Masters points
  playerChecklist?: { [playerId: string]: { showed: boolean; paid: boolean; paymentReference?: string; paymentReminderSentAt?: Date; discountType?: DiscountType; discountValue?: number; discountCode?: string } };
  giftsEnabled?: boolean;
  defaultGiftDescription?: string;
  defaultGiftDescriptions?: string[];
  giftRedemptions?: {
    [playerId: string]: {
      giftDescription?: string;
      redeemed: boolean;
      redeemedAt?: Date;
      redeemedByAdminId?: string;
      redeemedByAdminName?: string;
    };
  };
  paymentSettings?: {
    payInCash?: boolean; // True if players pay in cash at the venue (no payment emails)
    accountName?: string;
    bankName?: string;
    iban?: string;
    bic?: string;
    paypalLink?: string;
    paypalQrCodeUrl?: string;
    emailSubject?: string;
    emailMessage?: string;
    earlyBird?: TournamentPaymentWindow;
    normalRegistration?: TournamentPaymentWindow;
    lateRegistration?: TournamentPaymentWindow;
    refundPolicy?: TournamentRefundPolicy;
  };
}

export interface TournamentSponsor {
  name: string;
  logoUrl: string;
  websiteUrl?: string;
}
